import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { SxProps } from '@mui/system';
import { SanitizedHtml } from 'framework/utils';

interface TemplatePreviewProps {
  html: string;
  title?: string;
  showTitle?: boolean;
  sanitize?: boolean;
  minHeight?: string | number;
  maxHeight?: string | number;
  paperSx?: SxProps;
  containerSx?: SxProps;
  titleSx?: SxProps;
  sanitizeOptions?: DOMPurify.Config;
  className?: string;
  titleAction?: React.ReactNode;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  html,
  title = 'Preview',
  titleAction = null,
  showTitle = true,
  sanitize = true,
  minHeight = '300px',
  maxHeight = '70vh',
  paperSx = { p: 2, boxShadow: 'none', overflow: 'hidden' },
  containerSx = {},
  titleSx = {
    pb: '20px',
    textAlign: 'end',
    color: 'var(--mui-palette-primary-main)',
  },
  sanitizeOptions = {},
  className,
}) => {
  const contentHtml = sanitize ? DOMPurify.sanitize(html, sanitizeOptions) : html;

  return (
    <Paper sx={{ ...paperSx }} className={className}>
      {showTitle && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {titleAction}
          <Typography sx={{ ...titleSx }}>{title}</Typography>
        </Box>
      )}
      <Box
        sx={{
          p: 2,
          border: theme => `1px solid ${theme.palette.border?.default || theme.palette.divider}`,
          borderRadius: 1,
          minHeight,
          maxHeight,
          overflowY: 'auto',
          ...containerSx,
        }}
      >
        <SanitizedHtml data-testid="html-preview" html={contentHtml} />
      </Box>
    </Paper>
  );
};

export default TemplatePreview;
